'use client';

import { sendGTMEvent } from '@next/third-parties/google';
import { setCookie } from 'cookies-next';
import { useAtom } from 'jotai';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { acceptedCookiePolicyAtom, cookieModalAtom } from '~/atoms';
import {
  AcceptedCookiePolicyEnum,
  CookieSettingType,
  CookieTypeEnum,
} from '~/types-and-enums/cookieTypes';
import { cookieSettings, getInitiallySelectedCookies } from '~/utils/cookieUtils';
import CookieCard from './CookieCard';
import CookiePreferences from './CookiePreferences';
import { CookieStorageKey } from './constants';
import { ROUTES_MAP } from '~/constants/routes';

/**
 * Renders a cookie banner with Accept All or Customise user cookie preferences.
 */
const CookieBanner = () => {
  const pathname = usePathname();
  const [acceptedCookiePolicy, setAcceptedCookiePolicy] = useAtom(acceptedCookiePolicyAtom);
  
  // Hide cookie banner on internal suppliers page
  const shouldHideBanner = pathname?.includes(ROUTES_MAP.authSuppliers);

  const [isModalOpen, setIsModalOpen] = useAtom(cookieModalAtom);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialCookiePolicy = useMemo(() => acceptedCookiePolicy, []);
  const [selectedCookies, setSelectedCookies] = useState<CookieTypeEnum[]>(
    getInitiallySelectedCookies(acceptedCookiePolicy),
  );

  useEffect(() => {
    if (isModalOpen) {
      setSelectedCookies(getInitiallySelectedCookies(acceptedCookiePolicy));
    }
  }, [acceptedCookiePolicy, isModalOpen]);

  useEffect(() => {
    if (acceptedCookiePolicy !== initialCookiePolicy) {
      // Determine if we're in a secure environment (HTTPS)
      // This is needed for iframe compatibility (Salesforce embedding)
      const isSecureEnvironment = window.location.protocol === 'https:';
      const sameSiteValue = isSecureEnvironment ? 'None' : 'Lax';
      const secureFlag = isSecureEnvironment ? '; Secure' : '';
      const partitionedFlag = isSecureEnvironment ? '; Partitioned' : '';
      
      // Set cookie with iframe-compatible attributes
      document.cookie = `${CookieStorageKey}=${acceptedCookiePolicy}; path=/; SameSite=${sameSiteValue}${secureFlag}${partitionedFlag}`;
      setCookie('hasAcceptedAnalytics', acceptedCookiePolicy.includes(CookieTypeEnum.Analytics), {
        sameSite: isSecureEnvironment ? 'none' : 'lax',
        secure: isSecureEnvironment,
      });
      setCookie('hasAcceptedBehavioral', acceptedCookiePolicy.includes(CookieTypeEnum.Behavioral), {
        sameSite: isSecureEnvironment ? 'none' : 'lax',
        secure: isSecureEnvironment,
      });
      sendGTMEvent({
        event: 'E_setAcceptedCookiePolicy',
        user_cookies_hasAcceptedAnalytics: acceptedCookiePolicy.includes(CookieTypeEnum.Analytics),
        user_cookies_hasAcceptedBehavioral: acceptedCookiePolicy.includes(
          CookieTypeEnum.Behavioral,
        ),
      });
    }
  }, [initialCookiePolicy, acceptedCookiePolicy]);

  const currentCookiePolicy = useMemo(
    () =>
      cookieSettings
        .map((setting) => {
          if (setting.required) {
            return setting.type;
          }

          return selectedCookies.includes(setting.type) ? setting.type : null;
        })
        .filter(Boolean)
        .join('') as AcceptedCookiePolicyEnum,

    [selectedCookies],
  );

  const onOpen = useCallback(() => {
    setIsModalOpen(true);
  }, [setIsModalOpen]);

  const onClose = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleCookieSettingClick = useCallback((setting: CookieSettingType) => {
    if (setting.required) {
      return;
    }

    setSelectedCookies((prev) =>
      prev.includes(setting.type)
        ? prev.filter((cookie) => cookie !== setting.type)
        : [...prev, setting.type],
    );
  }, []);

  const handleConfirm = useCallback(() => {
    setAcceptedCookiePolicy(currentCookiePolicy);
    onClose();
  }, [currentCookiePolicy, setAcceptedCookiePolicy, onClose]);

  const handleAcceptAll = useCallback(() => {
    setAcceptedCookiePolicy(AcceptedCookiePolicyEnum.All);
  }, [setAcceptedCookiePolicy]);

  // Hide cookie banner on internal suppliers page
  if (shouldHideBanner) {
    return null;
  }

  return (
    <>
      <CookieCard
        onOpen={onOpen}
        acceptedCookiePolicy={acceptedCookiePolicy}
        onAcceptAll={handleAcceptAll}
      />
      <CookiePreferences
        isModalOpen={isModalOpen}
        onClose={onClose}
        handleCookieSettingClick={handleCookieSettingClick}
        handleConfirm={handleConfirm}
        selectedCookies={selectedCookies}
      />
    </>
  );
};

export default CookieBanner;
